import { useState } from "react";
import { useTranslation } from "react-i18next";

import { HTTP_STATUS_CODE } from "@constants/api.response.code";
import { CustomModalButtons } from "@constants/global.constants";

import DupontLogger from "@utils/DupontLogger";

import ConfirmationPopup, { DIALOG_TYPES } from "@components/ConfirmationPopup";
import WPButton from "@components/WPButton";
import { TOAST_NOTIFICATION_TYPES, useToastNotification } from "@components/WPToastNotification";

import { markJobAsCancelled, removeJobIdForProject } from "../hooks/mldJobMap.utils";

import { cancelMLDReport } from "@api/reportApi/reportApi";

/**
 * Renders a "Cancel Report Generation" button together with the confirmation
 * and success popups.  Drop-in replacement for wherever a cancel-report flow
 * is needed (header for other-project jobs, full-screen loader for the
 * current-project job).
 *
 * Props:
 *  jobId       — ID of the running report job to cancel
 *  projectId   — project whose entry should be removed from the job map on success
 *  projectName — displayed in the confirmation description (pass "" for current project)
 *  onSuccess   — optional callback invoked after the success popup is dismissed
 *  buttonClass — optional CSS class forwarded to WPButton
 */
export const MldCancelReport = ({ jobId, projectId, projectName = "", onSuccess, buttonClass, variant }) => {
  const { t } = useTranslation();
  const { showToastNotification } = useToastNotification();
  const Logger = DupontLogger("App");

  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleConfirmation = async buttonId => {
    setShowConfirmPopup(false);
    if (buttonId !== CustomModalButtons.CONFIRM) return;

    try {
      setIsCancelling(true);
      const response = await cancelMLDReport(jobId);
      if (response?.status === HTTP_STATUS_CODE.OK) {
        markJobAsCancelled(jobId); // stop the poll loop on its next iteration
        setShowSuccessPopup(true);
      } else {
        showToastNotification({ type: TOAST_NOTIFICATION_TYPES.ERROR, message: t("failedReportGeneration") });
      }
    } catch (error) {
      Logger.info("error cancelling MLD report:", error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleSuccessClose = buttonId => {
    setShowSuccessPopup(false);
    if (buttonId === CustomModalButtons.CONFIRM) {
      removeJobIdForProject(projectId);
      onSuccess?.();
    }
  };

  return (
    <>
      <WPButton
        customClass={buttonClass}
        variant={variant}
        onClick={() => setShowConfirmPopup(true)}
        disabled={isCancelling}
      >
        {t("cancelReportGeneration")}
      </WPButton>

      {showConfirmPopup && (
        <ConfirmationPopup
          header={`${t("cancelReportGeneration")}?`}
          description={t("cancelReportGenerationDescription", { projectName })}
          type={DIALOG_TYPES.WARNING}
          confirmBtn={t("yes")}
          cancelBtn={t("no")}
          onClose={handleConfirmation}
        />
      )}

      {showSuccessPopup && (
        <ConfirmationPopup
          header={t("reportCancelled")}
          description={t("reportCancelledDescription")}
          type={DIALOG_TYPES.SUCCESS}
          confirmBtn={t("ok")}
          onClose={handleSuccessClose}
        />
      )}
    </>
  );
};
